import { enums } from "@shared/constants";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import { Controller, useForm, ControllerRenderProps } from "react-hook-form";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Input from "@ui-kit/form/Input/Input.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useEffect, useState } from "react";
import useDebounce from "@shared/hooks/useDebounce";
import { CustomPriceInputProps } from "./CustomPriceInput.props";

const CustomPriceInput = (props: CustomPriceInputProps) => {
  const {
    onSave,
    minPrice,
    price,
    currency = enums.CURRENCY_MAP.CAD.id,
    innerRef,
    isFreeAllowed,
  } = props;
  const { t } = useTranslation("pwyc");
  const formattedMinPrice = (
    <CurrencyFormat value={minPrice} displayType="text" currency={currency} />
  );

  const [inputValue, setInputValue] = useState("");
  const [customPriceValue] = useDebounce(inputValue, 500);

  const customPriceSchema = yup.object().shape({
    price: yup
      .string()
      .test("max-digits", t("Price cannot exceed 8 digits"), (value) => {
        if (!value) return true;
        const digitsOnly = value.replace(/[^0-9]/g, "");
        return digitsOnly.length <= 8;
      })
      .test(
        "valid-price",
        () => (
          <>
            {formattedMinPrice} {t("minimum for custom price")}
          </>
        ),
        (value) => {
          const numValue = Number(value);
          if (isFreeAllowed && numValue === 0) return true;
          return numValue >= Number(minPrice);
        }
      ),
  });

  const { control, trigger, reset } = useForm({
    values: {
      price,
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore Update yup to latest version to fix this
    resolver: yupResolver(customPriceSchema),
  });

  const handleValueChange = useCallback(
    async (
      value: string | null,
      field: ControllerRenderProps<{ price: string | undefined }, "price">
    ) => {
      setInputValue(value || "");
      return field.onChange(value);
    },
    [setInputValue]
  );

  const handleSave = useCallback(async () => {
    const isValid = await trigger();

    if (isValid) {
      onSave({ price: customPriceValue });
    }
  }, [customPriceValue, trigger, onSave]);

  const resetFormErrors = useCallback(() => {
    reset({ price });
  }, [reset, price]);

  /** reset form errors on option click */
  useEffect(() => {
    if (innerRef) {
      innerRef.current = { resetFormErrors };
    }
  }, [innerRef, resetFormErrors]);

  /** save debounced custom price value */
  useEffect(() => {
    if (customPriceValue) {
      handleSave();
    }
  }, [customPriceValue]);

  return (
    <Flex flex="1 1 120px" width={{ base: "100%" }}>
      <form style={{ width: "100%" }}>
        <Controller
          name="price"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl isInvalid={fieldState.invalid}>
              <CurrencyFormat
                name={field.name}
                value={field.value}
                onValueChange={({ value }) => handleValueChange(value, field)}
                customInput={Input}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore define customInput props
                autoFocus={price?.length}
                placeholder={t("Custom")}
                data-testid="custom-price-input"
              />
              <FormErrorMessage
                data-testid="custom-price-error"
                display="inline"
              >
                {fieldState.error?.message}
              </FormErrorMessage>
            </FormControl>
          )}
        />
      </form>
    </Flex>
  );
};

export default CustomPriceInput;
